// pages/support/GettingStarted.jsx
import { useState } from 'react';

export default function GettingStarted() {
  const [activeStep, setActiveStep] = useState(0);
  const [showVideo, setShowVideo] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState(null);

  const steps = [
    {
      number: "1",
      title: "Create Your Account",
      description: "Sign up for free and complete your profile to get started with Adsurg",
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        </svg>
      ),
      time: "2 minutes",
      details: [
        "Enter your business details and contact information",
        "Verify your email address",
        "Set up two-factor authentication for security"
      ],
      tips: "Use a business email for better account management"
    },
    {
      number: "2",
      title: "Connect Your Ad Accounts",
      description: "Link your existing advertising platforms or create new campaigns",
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
        </svg>
      ),
      time: "5 minutes",
      details: [
        "Connect Google Ads, Facebook Ads, and other platforms",
        "Import existing campaigns or start fresh",
        "Set up conversion tracking pixels"
      ],
      tips: "You can connect multiple ad accounts from the same platform"
    },
    {
      number: "3",
      title: "Set Your Campaign Goals",
      description: "Define your objectives and target audience for optimal results",
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      time: "3 minutes",
      details: [
        "Choose campaign objectives (sales, leads, awareness)",
        "Define your target audience demographics",
        "Set budget and bidding strategy"
      ],
      tips: "Start with a smaller budget and scale gradually"
    },
    {
      number: "4",
      title: "Create Your Ads",
      description: "Design compelling ad creatives that convert",
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
        </svg>
      ),
      time: "10 minutes",
      details: [
        "Use our creative builder or upload your own",
        "Write engaging ad copy",
        "A/B test different variations"
      ],
      tips: "Test at least 3 different ad variations for best results"
    },
    {
      number: "5",
      title: "Launch & Optimize",
      description: "Go live and let our AI optimize your campaigns in real-time",
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      ),
      time: "Ongoing",
      details: [
        "Review and launch your campaigns",
        "Monitor performance in real-time",
        "Let AI make automatic optimizations"
      ],
      tips: "Check your dashboard daily for the first week"
    }
  ];

  const faqs = [
    {
      question: "How long does it take to see results?",
      answer: "Most campaigns show initial data within 24-48 hours. Significant results typically appear within 7-14 days as our AI learns and optimizes your campaigns."
    },
    {
      question: "Do I need a credit card to start?",
      answer: "No credit card required for the free trial. You only need to add payment information when you're ready to launch your first paid campaign."
    },
    {
      question: "Can I cancel anytime?",
      answer: "Yes, you can cancel your subscription at any time with no cancellation fees or penalties. Your data remains accessible for 30 days after cancellation."
    },
    {
      question: "What's the minimum budget required?",
      answer: "You can start with as little as ₹10,000/month. However, we recommend starting with at least ₹25,000/month for better optimization results."
    }
  ];

  const features = [
    {
      icon: (
        <svg className="w-12 h-12 text-[#00809D]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      ),
      title: "AI-Powered Optimization",
      description: "Automatic campaign optimization based on real-time performance data"
    },
    {
      icon: (
        <svg className="w-12 h-12 text-[#00809D]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      ),
      title: "Advanced Analytics",
      description: "Comprehensive dashboards with actionable insights"
    },
    {
      icon: (
        <svg className="w-12 h-12 text-[#00809D]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      ),
      title: "Smart Targeting",
      description: "Reach the right audience with precision targeting"
    },
    {
      icon: (
        <svg className="w-12 h-12 text-[#00809D]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      title: "Budget Control",
      description: "Complete control over spending with smart budget allocation"
    }
  ];

  return (
    <div className="min-h-screen bg-black pt-20 pb-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12 sm:mb-16">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-[#00809D] to-[#00a0c4] rounded-2xl mb-6 shadow-2xl shadow-[#00809D]/50">
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-6xl font-bold text-white mb-4">
            Getting Started with Adsurg
          </h1>
          <p className="text-lg sm:text-xl text-gray-400 max-w-3xl mx-auto">
            Follow these simple steps to launch your first successful advertising campaign in minutes
          </p>

          {/* Quick Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8 max-w-4xl mx-auto">
            <div className="bg-gradient-to-br from-gray-900 to-black border border-white/10 rounded-2xl p-4">
              <p className="text-3xl font-bold text-[#00809D] mb-1">20min</p>
              <p className="text-gray-400 text-xs sm:text-sm">Setup Time</p>
            </div>
            <div className="bg-gradient-to-br from-gray-900 to-black border border-white/10 rounded-2xl p-4">
              <p className="text-3xl font-bold text-[#00809D] mb-1">5</p>
              <p className="text-gray-400 text-xs sm:text-sm">Simple Steps</p>
            </div>
            <div className="bg-gradient-to-br from-gray-900 to-black border border-white/10 rounded-2xl p-4">
              <p className="text-3xl font-bold text-[#00809D] mb-1">24/7</p>
              <p className="text-gray-400 text-xs sm:text-sm">Support</p>
            </div>
            <div className="bg-gradient-to-br from-gray-900 to-black border border-white/10 rounded-2xl p-4">
              <p className="text-3xl font-bold text-[#00809D] mb-1">Free</p>
              <p className="text-gray-400 text-xs sm:text-sm">Trial Available</p>
            </div>
          </div>
        </div>

        {/* Quick Start Video */}
        <div className="mb-12 sm:mb-16 bg-gradient-to-br from-gray-900 to-black border border-white/10 rounded-3xl p-6 sm:p-8 shadow-2xl">
          <div className="aspect-video bg-gradient-to-br from-[#00809D]/20 to-[#00a0c4]/20 rounded-2xl flex items-center justify-center mb-6 relative overflow-hidden group">
            <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS13aWR0aD0iMC41IiBvcGFjaXR5PSIwLjEiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZ3JpZCkiLz48L3N2Zz4=')] opacity-30"></div>
            <button
              onClick={() => setShowVideo(true)}
              className="relative w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-r from-[#00809D] to-[#00a0c4] rounded-full flex items-center justify-center text-white hover:scale-110 transition-all shadow-2xl shadow-[#00809D]/50 group-hover:shadow-[#00809D]/70"
            >
              <svg className="w-8 h-8 sm:w-10 sm:h-10 ml-1" fill="currentColor" viewBox="0 0 24 24">
                <path d="M8 5v14l11-7z" />
              </svg>
            </button>
          </div>
          <div className="text-center">
            <h3 className="text-xl sm:text-2xl font-bold text-white mb-2">Watch Our Quick Start Guide</h3>
            <p className="text-gray-400 text-sm sm:text-base">5-minute video walkthrough of the complete setup process</p>
          </div>
        </div>

        {/* Progress Indicator (Mobile) */}
        <div className="lg:hidden mb-8 bg-gradient-to-br from-gray-900 to-black border border-white/10 rounded-2xl p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-white font-semibold text-sm">Setup Progress</span>
            <span className="text-[#00809D] font-bold text-sm">{activeStep + 1} of {steps.length}</span>
          </div>
          <div className="w-full bg-gray-800 rounded-full h-2">
            <div
              className="bg-gradient-to-r from-[#00809D] to-[#00a0c4] h-2 rounded-full transition-all duration-500"
              style={{ width: `${((activeStep + 1) / steps.length) * 100}%` }}
            ></div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar Navigation (Desktop) */}
          <div className="hidden lg:block">
            <div className="sticky top-24 bg-gradient-to-br from-gray-900 to-black border border-white/10 rounded-3xl p-6">
              <h3 className="text-white font-bold mb-4 text-lg">Quick Navigation</h3>
              <div className="space-y-2">
                {steps.map((step, index) => (
                  <button
                    key={index}
                    onClick={() => setActiveStep(index)}
                    className={`w-full text-left p-3 rounded-xl transition-all flex items-center gap-3 ${activeStep === index
                        ? 'bg-gradient-to-r from-[#00809D] to-[#00a0c4] text-white shadow-lg shadow-[#00809D]/50'
                        : 'text-gray-400 hover:bg-white/5 hover:text-white'
                      }`}
                  >
                    <div className="flex-shrink-0">
                      {step.icon}
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-sm">{step.title}</p>
                      <p className="text-xs opacity-75">{step.time}</p>
                    </div>
                  </button>
                ))}
              </div>

              {/* Progress */}
              <div className="mt-6 pt-6 border-t border-white/10">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-gray-400 text-sm">Progress</span>
                  <span className="text-[#00809D] font-bold text-sm">{((activeStep + 1) / steps.length * 100).toFixed(0)}%</span>
                </div>
                <div className="w-full bg-gray-800 rounded-full h-2">
                  <div
                    className="bg-gradient-to-r from-[#00809D] to-[#00a0c4] h-2 rounded-full transition-all duration-500"
                    style={{ width: `${((activeStep + 1) / steps.length) * 100}%` }}
                  ></div>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3 space-y-6 sm:space-y-8">
            {/* Steps */}
            {steps.map((step, index) => (
              <div
                key={index}
                className={`group bg-gradient-to-br from-gray-900 to-black border rounded-3xl p-6 sm:p-8 transition-all duration-300 ${activeStep === index
                    ? 'border-[#00809D]/50 shadow-2xl shadow-[#00809D]/20'
                    : 'border-white/10 hover:border-[#00809D]/30'
                  }`}
              >
                <div className="flex flex-col sm:flex-row items-start gap-6">
                  <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-br from-[#00809D] to-[#00a0c4] rounded-2xl flex items-center justify-center text-white font-bold text-2xl shadow-lg shadow-[#00809D]/50">
                    {step.icon}
                  </div>
                  <div className="flex-1 w-full">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-3 gap-2">
                      <h3 className="text-2xl font-bold text-white group-hover:text-[#00809D] transition-colors">
                        {step.title}
                      </h3>
                      <span className="inline-flex items-center gap-2 px-3 py-1 bg-[#00809D]/20 text-[#00809D] rounded-full text-sm font-semibold w-fit">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        {step.time}
                      </span>
                    </div>
                    <p className="text-gray-300 mb-4 text-base sm:text-lg">
                      {step.description}
                    </p>
                    <ul className="space-y-2 mb-4">
                      {step.details.map((detail, i) => (
                        <li key={i} className="flex items-start gap-3 text-gray-400 text-sm sm:text-base">
                          <svg className="w-5 h-5 text-[#00809D] flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          {detail}
                        </li>
                      ))}
                    </ul>
                    <div className="bg-[#00809D]/10 border border-[#00809D]/30 rounded-xl p-4 flex items-start gap-3">
                      <svg className="w-5 h-5 text-[#00809D] flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                      </svg>
                      <div>
                        <p className="text-[#00809D] font-semibold text-sm mb-1">Pro Tip</p>
                        <p className="text-gray-300 text-xs sm:text-sm">{step.tips}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {/* Navigation Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <button
                onClick={() => setActiveStep(Math.max(0, activeStep - 1))}
                disabled={activeStep === 0}
                className="flex-1 bg-gray-800 text-white px-6 py-4 rounded-xl font-semibold hover:bg-gray-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Previous Step
              </button>
              <button
                onClick={() => setActiveStep(Math.min(steps.length - 1, activeStep + 1))}
                disabled={activeStep === steps.length - 1}
                className="flex-1 bg-gradient-to-r from-[#00809D] to-[#00a0c4] text-white px-6 py-4 rounded-xl font-semibold hover:scale-105 transition-all shadow-lg shadow-[#00809D]/50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                Next Step
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Features Section */}
        <div className="mt-16">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-8 text-center">What You'll Get</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <div key={index} className="bg-gradient-to-br from-gray-900 to-black border border-white/10 rounded-2xl p-6 hover:border-[#00809D]/50 transition-all text-center group">
                <div className="flex items-center justify-center mb-4">
                  {feature.icon}
                </div>
                <h4 className="text-white font-bold mb-2 text-base sm:text-lg group-hover:text-[#00809D] transition-colors">{feature.title}</h4>
                <p className="text-gray-400 text-xs sm:text-sm">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* FAQs */}
        <div className="mt-16">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-8 text-center">Frequently Asked Questions</h2>
          <div className="max-w-3xl mx-auto space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-gradient-to-br from-gray-900 to-black border border-white/10 rounded-2xl overflow-hidden">
                <button
                  onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                  className="w-full p-6 text-left flex items-center justify-between hover:bg-white/5 transition-all"
                >
                  <span className="text-white font-semibold text-base sm:text-lg pr-4">{faq.question}</span>
                  <svg
                    className={`w-5 h-5 text-[#00809D] flex-shrink-0 transition-transform ${expandedFaq === index ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {expandedFaq === index && (
                  <div className="px-6 pb-6 text-gray-400 text-sm sm:text-base animate-in fade-in duration-300">
                    {faq.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="mt-16 text-center bg-gradient-to-r from-[#00809D]/10 to-transparent border border-[#00809D]/30 rounded-3xl p-8 sm:p-12">
          <h3 className="text-2xl sm:text-3xl font-bold text-white mb-4">
            Ready to Get Started?
          </h3>
          <p className="text-gray-400 mb-8 text-base sm:text-lg max-w-2xl mx-auto">
            Create your free account now and launch your first campaign in minutes. No credit card required.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center max-w-md mx-auto">
            <button className="flex-1 bg-gradient-to-r from-[#00809D] to-[#00a0c4] text-white px-8 py-4 rounded-xl font-semibold hover:scale-105 transition-all shadow-lg shadow-[#00809D]/50 flex items-center justify-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              Create Free Account
            </button>
            <button className="flex-1 bg-gray-800 text-white px-8 py-4 rounded-xl font-semibold hover:bg-gray-700 transition-all flex items-center justify-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              Schedule Demo
            </button>
          </div>
        </div>

        {/* Resources */}
        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          <div className="bg-gradient-to-br from-gray-900 to-black border border-white/10 rounded-2xl p-6 hover:border-[#00809D]/50 transition-all group">
            <div className="w-12 h-12 bg-gradient-to-br from-[#00809D] to-[#00a0c4] rounded-xl flex items-center justify-center mb-4 shadow-lg shadow-[#00809D]/30">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
            </div>
            <h4 className="text-white font-bold mb-2 text-base sm:text-lg group-hover:text-[#00809D] transition-colors">Documentation</h4>
            <p className="text-gray-400 text-sm mb-4">Comprehensive guides and tutorials</p>
            <button className="text-[#00809D] font-semibold text-sm flex items-center gap-1 group-hover:gap-2 transition-all">
              Browse Docs
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </button>
          </div>

          <div className="bg-gradient-to-br from-gray-900 to-black border border-white/10 rounded-2xl p-6 hover:border-[#00809D]/50 transition-all group">
            <div className="w-12 h-12 bg-gradient-to-br from-[#00809D] to-[#00a0c4] rounded-xl flex items-center justify-center mb-4 shadow-lg shadow-[#00809D]/30">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
            </div>
            <h4 className="text-white font-bold mb-2 text-base sm:text-lg group-hover:text-[#00809D] transition-colors">Live Chat</h4>
            <p className="text-gray-400 text-sm mb-4">Get instant help from our team</p>
            <button className="text-[#00809D] font-semibold text-sm flex items-center gap-1 group-hover:gap-2 transition-all">
              Start Chat
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </button>
          </div>

          <div className="bg-gradient-to-br from-gray-900 to-black border border-white/10 rounded-2xl p-6 hover:border-[#00809D]/50 transition-all group sm:col-span-2 lg:col-span-1">
            <div className="w-12 h-12 bg-gradient-to-br from-[#00809D] to-[#00a0c4] rounded-xl flex items-center justify-center mb-4 shadow-lg shadow-[#00809D]/30">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <h4 className="text-white font-bold mb-2 text-base sm:text-lg group-hover:text-[#00809D] transition-colors">Community</h4>
            <p className="text-gray-400 text-sm mb-4">Connect with other advertisers</p>
            <button className="text-[#00809D] font-semibold text-sm flex items-center gap-1 group-hover:gap-2 transition-all">
              Join Forum
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Video Modal */}
      {showVideo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/90 backdrop-blur-md animate-in fade-in duration-300">
          <div className="relative w-full max-w-4xl">
            <button
              onClick={() => setShowVideo(false)}
              className="absolute -top-12 right-0 text-white hover:text-[#00809D] transition-colors p-2"
            >
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <div className="aspect-video bg-gradient-to-br from-[#00809D]/20 to-[#00a0c4]/20 rounded-3xl flex items-center justify-center">
              <p className="text-white text-xl">Video Player Placeholder</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
